import { langchainConfig } from '../config/langchain';

export interface ContentGenerationRequest {
  prompt: string;
  documentType?: string;
  context?: string;
  maxLength?: number;
  style?: 'formal' | 'informal' | 'technical' | 'creative';
}

export interface ContentGenerationResponse {
  content: string;
  success: boolean;
  error?: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

class ContentGenerationService {
  private apiKey: string;
  private model: string;
  private baseUrl: string;

  constructor() {
    this.apiKey = langchainConfig.openai.apiKey;
    this.model = langchainConfig.openai.modelName;
    this.baseUrl = 'https://api.openai.com/v1/chat/completions';
  }

  /**
   * Gera conteúdo usando OpenAI
   */
  async generateContent(request: ContentGenerationRequest): Promise<ContentGenerationResponse> {
    try {
      if (!this.apiKey) {
        throw new Error('Chave da API OpenAI não configurada');
      }

      const systemPrompt = this.buildSystemPrompt(request);
      const userPrompt = this.buildUserPrompt(request);

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: this.model,
          messages: [
            {
              role: 'system',
              content: systemPrompt
            },
            {
              role: 'user',
              content: userPrompt
            }
          ],
          temperature: langchainConfig.openai.temperature,
          max_tokens: request.maxLength || langchainConfig.openai.maxTokens,
          stream: false
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Erro na API OpenAI: ${errorData.error?.message || response.statusText}`);
      }

      const data = await response.json();
      const generatedContent = data.choices[0]?.message?.content || '';

      return {
        content: generatedContent,
        success: true,
        usage: {
          promptTokens: data.usage?.prompt_tokens || 0,
          completionTokens: data.usage?.completion_tokens || 0,
          totalTokens: data.usage?.total_tokens || 0
        }
      };

    } catch (error) {
      console.error('=== DEBUG: Erro ao gerar conteúdo ===', error);
      
      return {
        content: '',
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido na geração de conteúdo'
      };
    }
  }

  /**
   * Constrói o prompt do sistema baseado no tipo de documento e estilo
   */
  private buildSystemPrompt(request: ContentGenerationRequest): string {
    let basePrompt = `Você é um assistente especializado em criação de documentos profissionais. 
    Sua função é gerar conteúdo de alta qualidade baseado no prompt fornecido pelo usuário.`;

    if (request.documentType) {
      basePrompt += `\n\nTipo de documento: ${request.documentType}`;
    }

    if (request.style) {
      const styleInstructions = {
        formal: 'Use linguagem formal, profissional e acadêmica. Mantenha tom sério e objetivo.',
        informal: 'Use linguagem acessível e amigável, mas ainda profissional.',
        technical: 'Use terminologia técnica apropriada e seja preciso nos detalhes.',
        creative: 'Use linguagem criativa e envolvente, mas mantenha a clareza e profissionalismo.'
      };
      basePrompt += `\n\nEstilo: ${styleInstructions[request.style]}`;
    }

    basePrompt += `\n\nDiretrizes importantes:
    - Gere conteúdo original e relevante
    - Mantenha a estrutura lógica e coerência
    - Use parágrafos bem organizados
    - Seja específico e detalhado quando apropriado
    - Mantenha o tom profissional
    - Respeite o comprimento solicitado`;

    return basePrompt;
  }

  /**
   * Constrói o prompt do usuário
   */
  private buildUserPrompt(request: ContentGenerationRequest): string {
    let prompt = request.prompt;

    if (request.context) {
      prompt += `\n\nContexto adicional: ${request.context}`;
    }

    if (request.maxLength) {
      prompt += `\n\nPor favor, gere um documento com aproximadamente ${request.maxLength} palavras.`;
    }

    return prompt;
  }

  /**
   * Valida se o serviço está configurado corretamente
   */
  isConfigured(): boolean {
    return !!this.apiKey && this.apiKey.length > 0;
  }

  /**
   * Obtém informações sobre a configuração atual
   */
  getConfigurationInfo(): { hasApiKey: boolean; model: string; temperature: number } {
    return {
      hasApiKey: this.isConfigured(),
      model: this.model,
      temperature: langchainConfig.openai.temperature
    };
  }
}

export const contentGenerationService = new ContentGenerationService(); 